import { z } from 'zod';

import { zISODate } from './common';

/* ─── Ввод ──────────────────────────────────────────────────────────────── */

export const reportPeriodSchema = z.object({
  from: zISODate,
  to: zISODate,
});
export type ReportPeriod = z.infer<typeof reportPeriodSchema>;

export const EXPORT_KINDS = ['transactions', 'payouts', 'people', 'bills'] as const;
export type ExportKind = (typeof EXPORT_KINDS)[number];

export const exportSchema = reportPeriodSchema.extend({
  kind: z.enum(EXPORT_KINDS),
});
export type ExportInput = z.infer<typeof exportSchema>;

/* ─── Ответы ────────────────────────────────────────────────────────────── */

export interface ReportBreakdownRow {
  id: string | null;
  name: string;
  color: string | null;
  amountMinor: number;
  /** Доля в процентах от итога стороны — считаем на сервере, чтобы совпадало везде. */
  share: number;
  count: number;
}

export interface ReportSummary {
  from: string;
  to: string;
  currency: string;

  incomeMinor: number;
  expenseMinor: number;
  netMinor: number;

  /** Выплаты людям за период — часть расхода, но её всегда спрашивают отдельно. */
  payoutsMinor: number;
  taxesMinor: number;

  incomeByCategory: ReportBreakdownRow[];
  expenseByCategory: ReportBreakdownRow[];
  byWallet: ReportBreakdownRow[];

  /** Помесячная динамика внутри периода. */
  months: { month: string; incomeMinor: number; expenseMinor: number; netMinor: number }[];

  /**
   * Валюты без курса: их суммы в итог не попали. Молча выкидывать деньги
   * из отчёта нельзя — пользователь должен видеть, чего не хватает.
   */
  missingRates: string[];
}

export interface PersonCostRow {
  personId: string;
  nickname: string;
  roleTitle: string | null;
  status: string;

  /** Начислено за период, без налогов и корректировок. */
  accruedMinor: number;
  /** Бонусы и штрафы отдельными строками начисления. */
  bonusMinor: number;
  penaltyMinor: number;
  taxMinor: number;
  /** Прочие расходы, отнесённые на человека: отель, перелёт, девайсы. */
  otherCostMinor: number;
  /** Сколько организация потратила на человека всего. */
  totalCostMinor: number;

  paidMinor: number;
  /** Остаток долга: начислено минус выплачено. Отрицательный — переплата. */
  debtMinor: number;
}

export interface PeopleReport {
  from: string;
  to: string;
  currency: string;
  rows: PersonCostRow[];
  totals: {
    accruedMinor: number;
    taxMinor: number;
    otherCostMinor: number;
    totalCostMinor: number;
    paidMinor: number;
    debtMinor: number;
  };
  missingRates: string[];
}
